import {
  GameBaseRunners,
  GameFullResponse,
  GamePlayerRef,
  LineUpPlayer,
  ResultingState,
} from "../types/gameFull";

/** Values from the Edit Player modal. */
export type PlayerEdit = {
  firstName: string;
  lastName: string;
  jerseyNumber?: string;
  hitSide: string;
  pitchSide: string;
};

/** "Kodi Robinson" -> { firstName: "Kodi", lastName: "Robinson" } */
export const splitPlayerName = (name: string) => {
  const [firstName = "", ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
};

export const toPlayerName = (
  edit: Pick<PlayerEdit, "firstName" | "lastName">,
) => `${edit.firstName.trim()} ${edit.lastName.trim()}`.trim();

/** Applies an Edit Player save to matching lineup/roster entries. */
export const applyPlayerEditToPlayers = (
  players: LineUpPlayer[],
  playerId: string,
  edit: PlayerEdit,
): LineUpPlayer[] =>
  players.map((player) =>
    player.playerId === playerId
      ? {
          ...player,
          name: toPlayerName(edit),
          jerseyNumber: edit.jerseyNumber,
          hitSide: edit.hitSide,
          pitchSide: edit.pitchSide,
        }
      : player,
  );

/**
 * Applies an Edit Player save to the game locally: the lineup entry plus
 * every reference to the player (PAs, pitch states, base runners, live game).
 * TODO: Persist via omnibus once a game lineup edit endpoint exists.
 */
export const applyPlayerEdit = (
  data: GameFullResponse,
  playerId: string,
  edit: PlayerEdit,
): GameFullResponse => {
  const playerName = toPlayerName(edit);

  const renameRef = <T extends GamePlayerRef | null | undefined>(ref: T): T =>
    ref?.playerId === playerId ? ({ ...ref, playerName } as T) : ref;

  const renameRunners = (runners: GameBaseRunners): GameBaseRunners =>
    Object.fromEntries(
      Object.entries(runners).map(([base, runner]) => [
        base,
        renameRef(runner),
      ]),
    );

  const editLineUp = (players: LineUpPlayer[]) =>
    applyPlayerEditToPlayers(players, playerId, edit);

  const renameState = (state: ResultingState): ResultingState => ({
    ...state,
    currentHitter: renameRef(state.currentHitter),
    currentPitcher: renameRef(state.currentPitcher),
    baseRunners: renameRunners(state.baseRunners),
  });

  return {
    ...data,
    lineUp: {
      home: editLineUp(data.lineUp.home),
      away: editLineUp(data.lineUp.away),
      extra: editLineUp(data.lineUp.extra),
    },
    plateAppearances: data.plateAppearances.map((pa) => ({
      ...pa,
      hitter: renameRef(pa.hitter),
      pitcher: renameRef(pa.pitcher),
    })),
    activities: data.activities.map((activity) =>
      activity.resultingState
        ? { ...activity, resultingState: renameState(activity.resultingState) }
        : activity,
    ),
    game: {
      ...data.game,
      currentHitter: renameRef(data.game.currentHitter),
      currentPitcher: renameRef(data.game.currentPitcher),
      baseRunners: renameRunners(data.game.baseRunners),
    },
  };
};

/** One player's jersey number set in a list of lineup or roster players. */
export const setPlayerJerseyNumber = (
  players: LineUpPlayer[],
  playerId: string,
  jerseyNumber: string,
): LineUpPlayer[] =>
  players.map((player) =>
    player.playerId === playerId ? { ...player, jerseyNumber } : player,
  );

/** Whether `value` is a jersey number omnibus can store: 0 to 999. */
export const isValidJerseyNumber = (value: string) => /^\d{1,3}$/.test(value);
